import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ArrowUpRight, Landmark } from 'lucide-react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import type { AccountView } from '../types';
import { money, signedMoney } from '../components/Format';
import { PurposeBadge, StatusBadge } from '../components/Status';
import { AccountSyncButton } from '../components/AccountSyncButton';

export function DashboardPage() {
  const accounts = useQuery({
    queryKey: ['accounts'],
    queryFn: () => api<AccountView[]>('/api/accounts'),
    staleTime: Infinity,
    refetchOnWindowFocus: false
  });
  const data = accounts.data ?? [];
  const totals = data.reduce((total, account) => {
    total.equity += Number(account.runtime.equity ?? 0);
    total.pnl += Number(account.runtime.unrealizedPnl ?? 0);
    total.exposure += account.runtime.positions.reduce((sum, position) => sum + Number(position.notionalUsd || 0), 0);
    total.positions += account.runtime.positions.length;
    if (account.runtime.status === 'healthy') total.healthy += 1;
    return total;
  }, { equity: 0, pnl: 0, exposure: 0, positions: 0, healthy: 0 });
  const pnl = signedMoney(totals.pnl);
  const attention = data.filter((account) => account.runtime.status !== 'healthy');
  const ready = accounts.data !== undefined;

  return (
    <div className="page-stack page-enter">
      <div className="page-heading">
        <div><h1>账户总览</h1><p>资金与持仓，一目了然</p></div>
        <AccountSyncButton disabled={accounts.isLoading} />
      </div>

      <section className="metric-grid overview-grid" aria-label="账户汇总">
        <article className="metric-card featured"><span>总权益 <small>USDT</small></span><strong>{ready ? money(totals.equity) : '—'}</strong></article>
        <article className="metric-card"><span>未实现盈亏 <small>USDT</small></span><strong className={ready ? pnl.tone : ''}>{ready ? pnl.text : '—'}</strong></article>
        <article className="metric-card"><span>持仓敞口 <small>USDT</small></span><strong>{ready ? money(totals.exposure) : '—'}</strong><em>{ready ? `${totals.positions} 个持仓` : '正在读取'}</em></article>
      </section>

      <section className="panel fleet-panel">
        <div className="panel-heading"><h2>我的账户</h2>{ready ? <span>{data.length} 个账户 · {totals.healthy} 个正常</span> : null}</div>
        {accounts.isLoading ? <div className="empty-state">正在加载账户…</div> : null}
        {accounts.error ? <div className="error-state">账户读取失败：{accounts.error.message}</div> : null}
        {ready && data.length === 0 ? <div className="empty-state">尚未配置账户</div> : null}
        {data.length > 0 ? <div className="account-table">
          <div className="account-row table-head" aria-hidden="true"><span>账户</span><span>状态</span><span>权益 / USDT</span><span>盈亏 / USDT</span><span>持仓</span></div>
          {data.map((account) => {
            const accountPnl = signedMoney(account.runtime.unrealizedPnl);
            const exposure = account.runtime.positions.reduce((sum, position) => sum + Number(position.notionalUsd || 0), 0);
            return (
              <Link to={`/accounts/${account.id}`} className="account-row" key={account.id} aria-label={`查看${account.name}详情`}>
                <span className="account-name"><i><Landmark size={20} /></i><span className="account-identity"><b>{account.name}</b><PurposeBadge purpose={account.purpose} /></span><ArrowUpRight className="account-arrow" size={18} /></span>
                <span className="account-status"><StatusBadge status={account.runtime.status} /></span>
                <span className="account-field"><span className="field-label">权益 / USDT</span><b className="numeric">{account.runtime.equity == null ? '—' : money(account.runtime.equity)}</b></span>
                <span className="account-field"><span className="field-label">盈亏 / USDT</span><b className={`numeric ${accountPnl.tone}`}>{account.runtime.unrealizedPnl == null ? '—' : accountPnl.text}</b></span>
                <span className="account-positions"><b>{account.runtime.positions.length} 个持仓</b><small>敞口 {money(exposure)} USDT</small></span>
              </Link>
            );
          })}
        </div> : null}
      </section>

      {attention.length > 0 ? <section className="panel notice-panel" aria-label="待处理账户">
        <div className="panel-heading"><h2>需要关注</h2><span>{attention.length} 个账户</span></div>
        {attention.map((account) => (
          <Link to={`/accounts/${account.id}`} className="notice-item" key={account.id}><AlertTriangle size={21} /><div><strong>{account.name}</strong><p>{account.runtime.lastError ?? '尚未完成首次同步'}</p></div><ArrowUpRight size={18} /></Link>
        ))}
      </section> : null}
    </div>
  );
}
